import React, { useState } from 'react';
import { useRecoilState } from 'recoil';
import { progressFlgState } from '../../GlobalState';
import { SelectIcon } from '../parts/cloudStorage/SelectIcon';
import {
  useStyles,
  ModalSubmitButton,
  ModalInnerButton
} from '../parts/cloudStorage/style/style';
import { DeleteModal } from './DeleteModal';
import { PreviewField } from '../fileListPage/Preview';
import {
  getFileDownload,
  editFileDetail,
  deleteFile
} from '../../apicalls/cloud/shareFile';
import { Grid, Modal, TextField } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CloseIcon from '@material-ui/icons/Close';
import {
  checkResData,
  formatDatetime,
  formatDataSize
} from '../../utils/common';
import { useSnackbar } from 'notistack';
import type { TSharedFile } from '../../types/cloudStorage/CloudFileList';

type TProps = {
  file: TSharedFile;
  setOpenDetailModal: React.Dispatch<React.SetStateAction<boolean>>;
  getSharedFileList: () => void;
  isGuestFile: boolean;
};

//資料詳細
export const FileDetailField: React.FC<TProps> = (props: TProps) => {
  const { file, setOpenDetailModal, getSharedFileList, isGuestFile } = props;

  // メッセージ
  const { enqueueSnackbar } = useSnackbar();
  // プレビュー
  const [src, setSrc] = useState<string>('');
  const [openPreview, setOpenPreview] = useState<boolean>(false);

  // 削除モーダル表示フラグ
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const [, setProgressFlg] = useRecoilState<boolean>(progressFlgState);

  const classes = useStyles();

  const [fileName, setFileName] = useState<string>(file.name);
  const [fileExchangeDate, setFileExchangeDate] = useState<string>(
    file.exchange_date
  );
  const [fileDescription, setFileDescription] = useState<string>(
    file.description
  );
  const fileFolderId = file.file_folder_id;

  const onDownload = async () => {
    if (!file) return;
    setProgressFlg(true);

    await getFileDownload(String(file.id), true).then((data: any) => {
      // 書類ダウンロード
      let bin = atob(data.data.replace(/^.*,/, ''));

      let buffer = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) {
        buffer[i] = bin.charCodeAt(i);
      }

      let fileData = new File([buffer.buffer], data.data, {
        type: data.file_format
      });

      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(fileData);
      const fileName = file.name;
      downloadLink.download = fileName;
      downloadLink.click();
    });

    setProgressFlg(false);
  };

  const PdfPreview = async () => {
    if (!file) return;
    setProgressFlg(true);

    await getFileDownload(String(file.id), false).then((data: any) => {
      let bin = atob(data.data.replace(/^.*,/, ''));

      let buffer = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) {
        buffer[i] = bin.charCodeAt(i);
      }

      let image_file = new File([buffer.buffer], data.data, {
        type: data.file_format
      });
      setSrc(URL.createObjectURL(image_file));
      setOpenPreview(true);
    });

    setProgressFlg(false);
  };

  // ファイル更新
  const onEditFile = async () => {
    await editFileDetail(
      file.id,
      fileName,
      file.construct_id,
      fileExchangeDate,
      fileFolderId,
      fileDescription
    ).then(resData => {
      checkResData(resData, enqueueSnackbar);
    });

    getSharedFileList();
    setOpenDetailModal(false);
  };

  // ファイル削除
  const onDeleteFile = async () => {
    await deleteFile(file.id).then(resData => {
      checkResData(resData, enqueueSnackbar);
    });

    getSharedFileList();
    setOpenDetailModal(false);
  };

  return (
    <>
      <form className={classes.paper}>
        <Grid container>
          {/* クローズボタン */}
          <Grid
            item
            xs={12}
            style={{ display: 'flex', justifyContent: 'end', padding: 12 }}
          >
            <CloseIcon
              style={{ fontSize: 36, cursor: 'pointer' }}
              onClick={() => {
                setOpenDetailModal(false);
              }}
            />
          </Grid>
          {/* モーダル左側 */}
          <Grid item xs={9} className={classes.modalLeft}>
            {/* ファイル名 */}
            <div className={classes.modalTitle}>
              <div style={{ marginLeft: 24, marginRight: 20 }}>
                <SelectIcon format={file.format} name={file.name} />
              </div>
              <div style={{ marginBottom: 8 }}>{file.name}</div>
            </div>

            {/* 入力 or 表示フォーム */}
            {/* 共有先がアップロードしたファイルの場合（isGuestFile=true）は編集可、他のユーザーのファイルは編集不可 */}
            <Grid container>
              <Grid item xs={12}>
                <Grid
                  container
                  alignItems='center'
                  style={{ marginTop: 12, padding: '8px 24px' }}
                >
                  <Grid item xs={2}>
                    <p style={{ margin: 0 }}>書類名</p>
                  </Grid>
                  <Grid item xs={5}>
                    {isGuestFile ? (
                      <TextField
                        style={{ width: '100%' }}
                        variant='outlined'
                        value={fileName}
                        onChange={e => {
                          setFileName(e.target.value);
                        }}
                      />
                    ) : (
                      <p style={{ margin: 0 }}>{fileName}</p>
                    )}
                  </Grid>
                </Grid>

                <Grid
                  container
                  alignItems='center'
                  style={{ padding: '8px 24px' }}
                >
                  <Grid item xs={2}>
                    <p style={{ margin: 0 }}>日付</p>
                  </Grid>
                  <Grid item xs={5}>
                    {isGuestFile ? (
                      <TextField
                        style={{ width: '100%' }}
                        variant='outlined'
                        type='date'
                        value={fileExchangeDate}
                        onChange={e => {
                          setFileExchangeDate(e.target.value);
                        }}
                      />
                    ) : (
                      <p style={{ margin: 0 }}>
                        {formatDatetime(
                          new Date(fileExchangeDate),
                          'YYYY年MM月DD日'
                        )}
                      </p>
                    )}
                  </Grid>
                </Grid>

                <Grid
                  container
                  alignItems='center'
                  style={{ padding: '8px 24px' }}
                >
                  <Grid item xs={2}>
                    <p style={{ margin: 0 }}>説明</p>
                  </Grid>
                  <Grid item xs={10}>
                    {isGuestFile ? (
                      <TextField
                        className={classes.description}
                        value={fileDescription}
                        rows={3}
                        multiline={true}
                        onChange={e => {
                          setFileDescription(e.target.value);
                        }}
                        variant='outlined'
                      />
                    ) : (
                      <p style={{ margin: 0 }}>{fileDescription}</p>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* モーダル右側 */}
          <Grid item xs={3} className={classes.modalRight}>
            <div>
              <span style={{ marginRight: 12, marginBottom: 8 }}>作成日</span>
              {formatDatetime(
                new Date(file.created_at),
                'YYYY.MM.DD',
                '日本標準時'
              )}
            </div>
            <div>
              <span style={{ marginRight: 12 }}>作成者</span>
              {file.created_name}
            </div>
            <div>
              <span style={{ marginRight: 12 }}>サイズ</span>
              {formatDataSize(file.size)}
            </div>

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              {file.format === 'application/pdf' && (
                <ModalInnerButton variant='contained' onClick={PdfPreview}>
                  <VisibilityIcon />
                  PDFプレビュー
                </ModalInnerButton>
              )}

              <ModalInnerButton variant='contained' onClick={onDownload}>
                <GetAppIcon />
                ダウンロード
              </ModalInnerButton>

              {isGuestFile && (
                <p
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => setOpenDeleteModal(true)}
                >
                  削除する
                </p>
              )}
            </div>
          </Grid>

          {/* モーダルフッター */}
          <Grid item xs={12} className={classes.modalFooter}>
            {isGuestFile ? (
              <ModalSubmitButton variant='contained' onClick={onEditFile}>
                更新する
              </ModalSubmitButton>
            ) : (
              <ModalSubmitButton
                variant='contained'
                onClick={() => setOpenDetailModal(false)}
              >
                閉じる
              </ModalSubmitButton>
            )}
          </Grid>
        </Grid>
      </form>

      {/* PDFプレビュー */}
      <Modal
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }}
        open={openPreview}
        onClose={() => setOpenPreview(false)}
      >
        <PreviewField
          src={src}
          openPreview={openPreview}
          setOpenPreview={setOpenPreview}
        />
      </Modal>

      {/* 資料削除モーダル */}
      <DeleteModal
        name={file.name}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        onDeleteFile={onDeleteFile}
      />
    </>
  );
};
